import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText, Mail, Globe2 } from "lucide-react";

const CONTENT = {
  fr: {
    lang_switch: "English",
    title: "Conditions d'utilisation",
    subtitle: "Un Avocat en Or — Robin Oschmann · BCE / TVA BE 0838.152.848",
    updated: "Dernière mise à jour : 3 juillet 2026",
    intro:
      "Bienvenue sur Un Avocat en Or. Ces conditions générales d'utilisation (« CGU ») régissent l'accès à notre application web et mobile ainsi qu'à ses services. En créant un compte ou en utilisant l'application, vous acceptez pleinement ces conditions.",
    sections: [
      {
        h: "1. Éditeur et service",
        p: [
          "Un Avocat en Or (« l'Application ») est édité par Robin Oschmann, entrepreneur individuel de droit belge, immatriculé BCE 0838.152.848, TVA BE 0838.152.848. Siège social : 54 rue Fond du Sart, 4171 Comblain-au-Pont, Belgique. Contact : contact@unavocatenor.com.",
          "L'Application propose une assistance juridique alimentée par intelligence artificielle, disponible en 13 langues, couvrant la Belgique, la France, le Luxembourg et la Suisse romande.",
        ],
      },
      {
        h: "2. Non-substitution à un avocat humain",
        p: [
          "IMPORTANT : Un Avocat en Or est un outil d'INFORMATION et d'ORIENTATION juridique. Il ne remplace en aucun cas la consultation d'un avocat, notaire, huissier, médiateur ou autre professionnel qualifié.",
          "Les réponses générées par l'IA sont fondées sur des textes de loi et de la jurisprudence publique, mais peuvent contenir des erreurs, être obsolètes, ou ne pas s'appliquer à votre situation spécifique. Vous êtes seul·e responsable des décisions que vous prenez sur la base des informations fournies.",
          "Pour toute procédure engagée devant une juridiction, un notaire ou une administration, consultez un professionnel du droit.",
        ],
      },
      {
        h: "3. Création de compte",
        p: [
          "Vous devez être âgé·e d'au moins 16 ans pour créer un compte. Vous vous engagez à fournir des informations exactes et à maintenir la confidentialité de vos identifiants.",
          "Un compte par personne. Le partage de compte est interdit, sauf dans le cadre d'un abonnement Famille (2 parents + enfants du même foyer).",
        ],
      },
      {
        h: "4. Formules et paiement",
        p: [
          "Formule Découverte : gratuite, usage modéré (5 requêtes/mois environ). Formule Individuel : 9,50 € TTC/mois. Formule Famille : 14,50 € TTC/mois (2 parents + enfants illimités). Packs B2B (TPE, PME, Corporate, Grand Compte) : voir grille sur unavocatenor.com.",
          "Paiements traités par Stripe. Prélèvement automatique mensuel jusqu'à résiliation. Résiliation en 1 clic depuis votre profil, effective à la fin du cycle en cours. Aucun remboursement pour les mois entamés, sauf cas prévus par le droit belge de la consommation.",
          "Comptes VIP (bénévoles, testeurs, partenaires) : gratuité totale, révocable à tout moment.",
        ],
      },
      {
        h: "5. Usage interdit",
        p: [
          "Vous vous engagez à NE PAS utiliser l'Application pour :",
          "• Nuire à autrui (harcèlement, diffamation, chantage).",
          "• Contourner la loi ou préparer un acte illégal.",
          "• Diffuser des contenus haineux, sexuellement explicites, ou incitant à la violence.",
          "• Automatiser des requêtes en masse (scraping, bots).",
          "• Usurper l'identité d'un tiers.",
          "• Solliciter des mineur·es via le réseau Refuge.",
          "Tout manquement entraîne suspension immédiate du compte, sans remboursement.",
        ],
      },
      {
        h: "6. Réseau Refuge solidaire",
        p: [
          "Le réseau Refuge est un service optionnel permettant à des victimes (agression, LGBTQI+phobie, violences conjugales) de trouver un hébergement d'urgence.",
          "Pour devenir hôte, vous devez : être majeur·e, avoir un compte vérifié depuis 30 jours, accepter la Charte de sécurité. Les hôtes sont soumis à modération manuelle avant activation.",
          "3 signalements confirmés entraînent une suspension automatique du compte hôte. En cas de dépôt de plainte pénale, nous coopérerons avec les autorités judiciaires belges/européennes.",
          "L'Application n'est PAS responsable des interactions individuelles entre hôtes et personnes hébergées. Chacun agit sous sa propre responsabilité.",
        ],
      },
      {
        h: "7. Propriété intellectuelle",
        p: [
          "Le nom « Un Avocat en Or », le logo blason, la charte graphique, les prompts IA, le code source, et les traductions sont la propriété exclusive de Robin Oschmann.",
          "Vous conservez la propriété du contenu que vous soumettez (questions, documents, enregistrements). Vous nous accordez une licence non-exclusive, mondiale, gratuite, uniquement pour vous fournir le service (analyse par IA, stockage sécurisé).",
        ],
      },
      {
        h: "8. Responsabilité",
        p: [
          "L'Application est fournie « telle quelle ». Nous nous efforçons d'assurer une disponibilité maximale, mais ne garantissons pas l'absence d'interruption, d'erreur ou de faille.",
          "Notre responsabilité est limitée, dans la mesure permise par la loi belge, aux montants effectivement payés par vous durant les 12 derniers mois.",
          "Nous ne sommes pas responsables des dommages indirects (perte de chance, préjudice moral, préjudice économique) résultant de l'usage ou de l'inaccessibilité du service.",
        ],
      },
      {
        h: "9. Modification des CGU",
        p: [
          "Nous pouvons modifier ces CGU pour refléter des évolutions légales ou fonctionnelles. Toute modification substantielle est notifiée par email avec un préavis raisonnable. Votre poursuite d'utilisation vaut acceptation.",
        ],
      },
      {
        h: "10. Résiliation",
        p: [
          "Vous pouvez résilier votre compte à tout moment depuis votre profil ou par email à contact@unavocatenor.com. Vos données sont supprimées sous 30 jours, sauf obligations légales de conservation.",
          "Nous pouvons résilier un compte en cas de manquement grave aux CGU, sans préavis, sans indemnité.",
        ],
      },
      {
        h: "11. Droit applicable et juridiction",
        p: [
          "Ces CGU sont régies par le droit belge. Tout litige relève exclusivement des tribunaux de l'arrondissement judiciaire de Liège, Belgique, sauf dispositions impératives contraires du droit de la consommation.",
          "Les consommateurs de l'Union européenne peuvent recourir à la plateforme de règlement en ligne des litiges : ec.europa.eu/consumers/odr.",
        ],
      },
      {
        h: "12. Contact",
        p: [
          "Une question, une réclamation ? Écrivez à contact@unavocatenor.com. Réponse sous 5 jours ouvrables.",
        ],
      },
    ],
  },
  en: {
    lang_switch: "Français",
    title: "Terms of Service",
    subtitle: "A Lawyer in Gold — Robin Oschmann · VAT BE 0838.152.848",
    updated: "Last updated: 3 July 2026",
    intro:
      "Welcome to A Lawyer in Gold. These Terms of Service (\"Terms\") govern access to our web and mobile application and its services. By creating an account or using the app, you fully accept these Terms.",
    sections: [
      {
        h: "1. Publisher and service",
        p: [
          "A Lawyer in Gold (\"the App\") is operated by Robin Oschmann, sole proprietor under Belgian law, registered CBE 0838.152.848, VAT BE 0838.152.848. Registered office: 54 rue Fond du Sart, 4171 Comblain-au-Pont, Belgium. Contact: contact@unavocatenor.com.",
          "The App provides AI-powered legal assistance in 13 languages, covering Belgium, France, Luxembourg and French-speaking Switzerland.",
        ],
      },
      {
        h: "2. No substitute for a human lawyer",
        p: [
          "IMPORTANT: A Lawyer in Gold is an INFORMATION and ORIENTATION tool. It does not replace consultation with a lawyer, notary, bailiff, mediator or any other qualified professional.",
          "AI-generated answers are based on public statutes and case law but may contain errors, be outdated, or not apply to your specific situation. You alone are responsible for decisions based on the information provided.",
          "For any proceeding before a court, notary or administration, consult a legal professional.",
        ],
      },
      {
        h: "3. Account creation",
        p: [
          "You must be at least 16 years old to create an account. You commit to providing accurate information and maintaining the confidentiality of your credentials.",
          "One account per person. Account sharing is prohibited, except under a Family subscription (2 parents + children from the same household).",
        ],
      },
      {
        h: "4. Plans and payment",
        p: [
          "Discovery plan: free, light use (approx. 5 requests/month). Individual plan: €9.50/month. Family plan: €14.50/month (2 parents + unlimited children). B2B packs (Small Business, SME, Corporate, Enterprise): see pricing on unavocatenor.com.",
          "Payments processed by Stripe. Automatic monthly billing until cancellation. One-click cancellation from your profile, effective at the end of the current cycle. No refunds for started months, except as required by Belgian consumer law.",
          "VIP accounts (volunteers, testers, partners): fully free, revocable at any time.",
        ],
      },
      {
        h: "5. Prohibited use",
        p: [
          "You commit NOT to use the App to:",
          "• Harm others (harassment, defamation, blackmail).",
          "• Circumvent the law or prepare an illegal act.",
          "• Distribute hateful, sexually explicit or violence-inciting content.",
          "• Automate mass queries (scraping, bots).",
          "• Impersonate a third party.",
          "• Solicit minors via the Refuge network.",
          "Any breach leads to immediate account suspension, without refund.",
        ],
      },
      {
        h: "6. Solidarity Refuge network",
        p: [
          "The Refuge network is an optional service enabling victims (assault, LGBTQI+phobia, domestic violence) to find emergency shelter.",
          "To become a host you must: be of legal age, have a verified account for 30 days, accept the Safety Charter. Hosts undergo manual moderation before activation.",
          "3 confirmed reports trigger automatic suspension of the host account. In case of criminal complaint, we will cooperate with Belgian/European judicial authorities.",
          "The App is NOT responsible for individual interactions between hosts and hosted persons. Each party acts under their own responsibility.",
        ],
      },
      {
        h: "7. Intellectual property",
        p: [
          "The name \"A Lawyer in Gold\", the shield logo, the graphic identity, the AI prompts, source code, and translations are the exclusive property of Robin Oschmann.",
          "You retain ownership of content you submit (questions, documents, recordings). You grant us a non-exclusive, worldwide, free license, solely to provide the service (AI analysis, secure storage).",
        ],
      },
      {
        h: "8. Liability",
        p: [
          "The App is provided \"as is\". We strive to ensure maximum availability but do not guarantee the absence of interruption, error or defect.",
          "Our liability is limited, to the extent permitted by Belgian law, to amounts effectively paid by you in the last 12 months.",
          "We are not liable for indirect damages (loss of opportunity, moral damage, economic damage) resulting from use or inaccessibility of the service.",
        ],
      },
      {
        h: "9. Changes to the Terms",
        p: [
          "We may modify these Terms to reflect legal or functional changes. Any material change is notified by email with reasonable notice. Your continued use constitutes acceptance.",
        ],
      },
      {
        h: "10. Termination",
        p: [
          "You may terminate your account at any time from your profile or by email to contact@unavocatenor.com. Your data will be deleted within 30 days, except legal retention obligations.",
          "We may terminate an account for serious breach of the Terms, without notice, without compensation.",
        ],
      },
      {
        h: "11. Governing law and jurisdiction",
        p: [
          "These Terms are governed by Belgian law. Any dispute falls under the exclusive jurisdiction of the courts of the judicial district of Liège, Belgium, except for mandatory consumer law provisions.",
          "EU consumers may use the online dispute resolution platform: ec.europa.eu/consumers/odr.",
        ],
      },
      {
        h: "12. Contact",
        p: [
          "A question, a complaint? Write to contact@unavocatenor.com. Response within 5 business days.",
        ],
      },
    ],
  },
};

export default function Terms() {
  const [lang, setLang] = useState("fr");
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="terms-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition"
            data-testid="terms-lang-toggle"
          >
            <Globe2 className="h-3.5 w-3.5" />
            {c.lang_switch}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-sm bg-amber-100 text-amber-800 border border-amber-300 flex items-center justify-center">
            <ScrollText className="h-5 w-5" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif text-slate-900" data-testid="terms-title">
            {c.title}
          </h1>
        </div>
        <p className="text-sm text-slate-600">{c.subtitle}</p>
        <p className="text-xs text-slate-500 mt-1">{c.updated}</p>

        <p className="mt-6 text-base text-slate-700 leading-relaxed">{c.intro}</p>

        <div className="mt-8 space-y-8">
          {c.sections.map((s, idx) => (
            <section key={idx} data-testid={`terms-section-${idx}`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">{s.h}</h2>
              <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
                {s.p.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 p-4 rounded-sm bg-amber-50 border border-amber-200 flex items-start gap-3">
          <Mail className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>{lang === "fr" ? "Une question ?" : "A question?"}</strong>{" "}
            <a href="mailto:contact@unavocatenor.com" className="underline hover:no-underline">
              contact@unavocatenor.com
            </a>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-4 text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-900 underline">
            {lang === "fr" ? "Politique de confidentialité" : "Privacy Policy"}
          </Link>
          <Link to="/media-kit" className="hover:text-slate-900 underline">
            {lang === "fr" ? "Kit presse" : "Media Kit"}
          </Link>
          <Link to="/" className="hover:text-slate-900 underline">
            unavocatenor.com
          </Link>
        </div>
      </main>
    </div>
  );
}
